import { useSyncExternalStore } from "react";
import { parseBundleVersion } from "./stale-bundle";

// The client bundle filename (index-<hash>.js) is the deploy version. We learn
// the server's CURRENT version from the X-App-Version header on every API
// response (and a light interval poll as backstop for an idle-but-focused tab).
// When it no longer matches the bundle THIS tab is running, we flag "update
// available" so a non-destructive banner can offer a reload.
//
// We deliberately never auto-reload here: that could discard a half-filled
// order form. (stale-bundle.ts handles the separate refocus auto-reload case.)

function readOwnVersion(): string | null {
  if (typeof document === "undefined") return null;
  for (const tag of Array.from(
    document.querySelectorAll<HTMLScriptElement>("script[src]"),
  )) {
    const v = parseBundleVersion(tag.src);
    if (v) return v;
  }
  // Dev: vite serves /src/main.tsx, no hashed bundle -> check disabled.
  return null;
}

const ownVersion = readOwnVersion();

// Pure + testable: a newer version is live only when both sides are known and
// differ. Unknown own (dev) or unknown server -> never outdated.
export function isOutdated(
  own: string | null,
  server: string | null | undefined,
): boolean {
  return !!own && !!server && own !== server;
}

let updateAvailable = false;
const listeners = new Set<() => void>();

export function reportServerVersion(server: string | null | undefined): void {
  if (updateAvailable) return; // sticky: once outdated, stay outdated
  if (isOutdated(ownVersion, server)) {
    updateAvailable = true;
    for (const listener of listeners) listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return updateAvailable;
}

export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Backstop for a tab kept focused with no API activity: poll the version
// endpoint on a light interval. Non-destructive (only sets the flag).
let pollStarted = false;
export function initVersionPolling(intervalMs = 5 * 60_000): void {
  if (pollStarted || !ownVersion) return; // disabled in dev (no own version)
  pollStarted = true;
  const check = async () => {
    if (updateAvailable) return;
    try {
      const res = await fetch("/api/version", { cache: "no-store" });
      if (!res.ok) return;
      const body = await res.json();
      reportServerVersion(typeof body?.version === "string" ? body.version : null);
    } catch {
      /* offline/transient: retry on next tick */
    }
  };
  setInterval(check, intervalMs);
}
